import { useCallback } from 'react';
import { useApolloClient } from '@apollo/client';
import { useQuery, useQueryClient } from 'react-query';
import { err, ok, type Result } from 'neverthrow';

import { AgentThreadsDocument, useAgentThreadRenameMutation, useAgentThreadsQuery } from 'src/generated/mloop-graphql';

import { type AgentTokenProvider, getAgentThreadHistory } from './agentClient';
import { toChatItemsFromHistory } from './chatItems';
import type { AgentThreadSummary } from './protocol';

/** React Query cache namespace for agent thread reads. */
const AGENT_QUERY_KEY = 'AGENT_THREADS';
const AGENT_THREAD_HISTORY_KEY = 'HISTORY';
const AGENT_THREADS_INPUT = { pageInput: { limit: 20 } } as const;

export type AgentThreadRenameFailure = {
  type: 'INVALID_TITLE' | 'NOT_FOUND' | 'UNKNOWN';
  message: string;
};

const AGENT_THREAD_LIST_STATUS = {
  ERROR: 'ERROR',
  LOADING: 'LOADING',
  SUCCESS: 'SUCCESS',
} as const;

type AgentThreadListStatus = (typeof AGENT_THREAD_LIST_STATUS)[keyof typeof AGENT_THREAD_LIST_STATUS];

/** A thread-list read with an explicit distinction between empty successful data and an unavailable list. */
export type AgentThreadListRead = {
  threads: readonly AgentThreadSummary[];
  status: AgentThreadListStatus;
};

/** Fetches the caller's agent threads for the history switcher. */
export function useAgentThreadList(
  enabled: boolean,
  useThreadListQuery: typeof useAgentThreadsQuery = useAgentThreadsQuery
): AgentThreadListRead {
  const { data, error } = useThreadListQuery({
    variables: { input: AGENT_THREADS_INPUT },
    skip: !enabled,
  });
  if (data) {
    return {
      threads: data.agentThreads.items.map((thread) => ({
        ...thread,
        title: thread.title ?? null,
      })),
      status: AGENT_THREAD_LIST_STATUS.SUCCESS,
    };
  }
  if (error) {
    return { threads: [], status: AGENT_THREAD_LIST_STATUS.ERROR };
  }
  return { threads: [], status: AGENT_THREAD_LIST_STATUS.LOADING };
}

/**
 * Fetches persisted messages for a thread and maps them to transcript items.
 *
 * A thread the user has not messaged yet does not exist server-side, so the request resolves to an
 * empty transcript instead of an error.
 */
export function useAgentThreadHistory(
  threadId: string,
  getToken: AgentTokenProvider,
  enabled: boolean,
  onSettled?: (threadId: string) => void,
  getHistory: typeof getAgentThreadHistory = getAgentThreadHistory
) {
  const { data, isLoading } = useQuery(
    [AGENT_QUERY_KEY, AGENT_THREAD_HISTORY_KEY, threadId],
    async () => toChatItemsFromHistory(await getHistory(threadId, getToken)),
    {
      enabled,
      // liveItems owns the turn until the user leaves this thread. Automatic focus/reconnect
      // refetches would otherwise append that just-persisted turn a second time.
      refetchOnReconnect: false,
      refetchOnWindowFocus: false,
      onSettled: () => onSettled?.(threadId),
    }
  );
  return { historyItems: data ?? [], isLoading };
}

/** Returns a callback that refreshes the thread list after a turn settles. */
export function useRefreshAgentThreadList(
  apolloClientOverride?: Pick<ReturnType<typeof useApolloClient>, 'refetchQueries'>
): () => void {
  const defaultApolloClient = useApolloClient();
  const apolloClient = apolloClientOverride ?? defaultApolloClient;
  return useCallback(() => {
    void apolloClient.refetchQueries({ include: [AgentThreadsDocument] });
  }, [apolloClient]);
}

/** Renames one owned Agent thread and refreshes the GraphQL thread list. */
export function useAgentThreadRename(): (
  threadId: string,
  title: string
) => Promise<Result<void, AgentThreadRenameFailure>> {
  const apolloClient = useApolloClient();
  const [agentThreadRename] = useAgentThreadRenameMutation({
    refetchQueries: [AgentThreadsDocument],
  });
  return useCallback(
    async (threadId, title) => {
      const result = await agentThreadRename({
        variables: { input: { externalThreadId: threadId, title } },
      });
      const output = result.data?.agentThreadRename;
      switch (output?.__typename) {
        case 'AgentThreadRenameSuccess':
          apolloClient.cache.updateQuery(
            { query: AgentThreadsDocument, variables: { input: AGENT_THREADS_INPUT } },
            (cached) =>
              cached
                ? {
                    agentThreads: {
                      ...cached.agentThreads,
                      items: cached.agentThreads.items.map((thread) =>
                        thread.externalThreadId === output.externalThreadId
                          ? { ...thread, title: output.title }
                          : thread
                      ),
                    },
                  }
                : cached
          );
          return ok(undefined);
        case 'AgentThreadRenameInvalidTitleError':
          return err({ type: 'INVALID_TITLE', message: output.errorMessage });
        case 'AgentThreadRenameNotFoundError':
          return err({ type: 'NOT_FOUND', message: output.errorMessage });
        case 'UnknownError':
        case undefined:
          return err({
            type: 'UNKNOWN',
            message: output?.errorMessage ?? 'Agent thread rename returned no result.',
          });
        default: {
          const exhaustive: never = output;
          return exhaustive;
        }
      }
    },
    [agentThreadRename, apolloClient.cache]
  );
}

/**
 * Returns a callback that marks a thread's persisted history stale without refetching the mounted
 * query.
 *
 * Call it for the thread being left on a switch: the active transcript keeps its fresh cache, so a
 * turn that streamed this session is never concatenated with a refetch of itself and rendered twice.
 * Because the history query is only invalidated once it is no longer the active thread, returning to
 * it later remounts a stale query and refetches, picking up turns that were persisted after the
 * history first loaded — otherwise the 1-minute `staleTime` would serve a cached transcript missing
 * the latest turn (Bugbot FEAT-1698).
 */
export function useInvalidateAgentThreadHistory(): (threadId: string) => void {
  const queryClient = useQueryClient();
  return useCallback(
    (threadId: string) => {
      void queryClient.invalidateQueries([AGENT_QUERY_KEY, AGENT_THREAD_HISTORY_KEY, threadId], {
        refetchActive: false,
      });
    },
    [queryClient]
  );
}
